import { useMemo } from "react";
import { useAppSelector } from "@/store/app/hooks";
import {
  type UserFeatureType,
  type UserProfileCanAccess,
} from "@/constants/userFeatureType";
import type { UserProfileState } from "@/types/userProfile";
import type { PermissionGrants } from "@/lib/permissions/permissionGrantsModel";
import { getPermissions } from "@/lib/permissions/getPermissions";
import { isUserProfileSessionReady } from "@/lib/session/isUserProfileSessionReady";

export type UserProfile = UserProfileState;

export type UseUserProfileReturn = UserProfileState & {
  permissionGrants: PermissionGrants;
  canAccess: UserProfileCanAccess;
  isExagens: boolean;
  isReady: boolean;
  canAccessFeature: (feature: UserFeatureType) => boolean;
};

export const useUserProfile = (): UseUserProfileReturn => {
  const userProfile = useAppSelector((state) => state.user.userProfile);
  const featuresData = useAppSelector((state) => state.user.features.data);
  const isReady =
    isUserProfileSessionReady(userProfile) && featuresData != null;

  const { permissionGrants, canAccess, isExagens } = useMemo(
    () => getPermissions(userProfile, featuresData ?? {}),
    [userProfile, featuresData],
  );

  return useMemo(
    () => ({
      ...userProfile,
      permissionGrants,
      canAccess,
      isExagens,
      isReady,
      canAccessFeature: (feature: UserFeatureType) => canAccess[feature],
    }),
    [userProfile, isReady, permissionGrants, canAccess, isExagens],
  );
};
